import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Formik, FormikErrors } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import { useLoginUserMutation } from "../../services/appln";
import { doesPermissionExist } from "../util";

const Logo = require("../../assets/logo.png");

interface FormData {
  userName: string;
  passWord: string;
}

export default function LoginForm() {
  const defaultTheme = createTheme();

  const navigate = useNavigate();
  const [loginUser, params] = useLoginUserMutation();

  const initialValues: FormData = { userName: "", passWord: "" };

  if (params.isUninitialized || params.isError) {
    return (
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors: FormikErrors<FormData> = {};
          if (!values.userName) {
            errors.userName = strings.usernameMandatory;
          }
          if (!values.passWord) {
            errors.passWord = strings.passwordMandatory;
          }
          return errors;
        }}
        onSubmit={(values, actions) => {
          console.log({ values, actions });
          actions.setSubmitting(false);
          loginUser(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <ThemeProvider theme={defaultTheme}>
            <Container component="main" maxWidth="xs">
              <CssBaseline />
              <Box
                sx={{
                  marginTop: 8,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={Logo}
                  width={120}
                  height={120}
                  className="logo"
                  alt="logo"
                />
                <Typography component="h1" variant="h5">
                  {strings.login}
                </Typography>
                {(params.isLoading || params.isFetching) && (
                  <ProgressIndicator />
                )}
                {params.isError ? (
                  <div>
                    <Alert severity="error">
                      <AlertTitle>{strings.loginFailedTitle}</AlertTitle>
                      {strings.loginFailedMsg}
                    </Alert>
                  </div>
                ) : null}
                <form onSubmit={handleSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="userName"
                    name="userName"
                    label={strings.username}
                    value={values.userName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.userName && Boolean(errors.userName)}
                    helperText={touched.userName && errors.userName}
                    autoFocus
                  />
                  <TextField
                    fullWidth
                    required
                    margin="normal"
                    id="passWord"
                    name="passWord"
                    type="password"
                    label={strings.password}
                    value={values.passWord}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.passWord && Boolean(errors.passWord)}
                    helperText={touched.passWord && errors.passWord}
                  />
                  <Button
                    fullWidth
                    id="loginSubmit"
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    {strings.login}
                  </Button>
                </form>
              </Box>
            </Container>
          </ThemeProvider>
        )}
      </Formik>
    );
  }

  if (!params.isUninitialized && params.isLoading) {
    // application is being created. show progress indicator
    // backdrop is shown over empty screen...
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!params.isUninitialized && params.isSuccess) {
    // user logged in. show home page.
    localStorage.setItem("accessToken", params.data.accessToken);
    localStorage.setItem("refreshToken", params.data.refreshToken);
    if (doesPermissionExist("read-bandobast")) {
      navigate("/bandobast");
    } else {
      navigate("/schedules");
    }
  }
  return <div></div>;
}
